import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Popper } from 'react-popper'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import TextInput from './text-input'
import { Dropdown } from 'react-bootstrap'
import OutsideClickHandler from 'react-outside-click-handler'
import { Trans } from 'react-i18next'
import IconButton from './icon-button'

// TODO: add uncontrolled behaviour
const DropdownSelect = ({
  value,
  children,
  items,
  groupedItems,
  onClick,
  className,
  style,
  id,
  asDiv,
  disabled,
  placeholder,
  searchable,
  onSearchChange,
  type = 'text',
}: {
  value?: string
  children?: JSX.Element | JSX.Element[]
  items?: { value: string | number; name?: string | number }[]
  groupedItems?: { [key: string]: { value: string | number; name?: string | number; type?: string | number }[] }
  onClick?: (value: string) => void
  onSearchChange?: (value: string) => void
  className?: string
  style?: React.CSSProperties
  id?: string
  asDiv?: boolean
  disabled?: boolean
  placeholder?: string
  searchable?: boolean
  type?: string
}) => {
  const [isOpen, setOpen] = useState(false)
  const { _t } = useTypedTranslation()
  const referenceElement = useRef<HTMLDivElement>(null)
  const chevronId = id ? id + '-chevron' : undefined
  const divValueId = id ? id + '-value' : undefined
  const [searchQuery, setSearchQuery] = useState('')

  // Sync searchQuery with value prop for searchable dropdowns
  useEffect(() => {
    if (searchable && value !== undefined) {
      setSearchQuery(value)
    }
  }, [value, searchable])

  useEffect(() => {
    if (searchable) {
      onSearchChange?.(searchQuery)
    }
  }, [searchQuery, searchable, onSearchChange])

  // Filtering for searchbar
  const filteredItems = useMemo(() => {
    if (!searchable) {
      return
    }
    if (items) {
      return items?.filter((item) => item.name?.toString().toLowerCase().includes(searchQuery.toLowerCase()))
    }
    if (groupedItems) {
      const filtered: { [key: string]: { value: string | number; name?: string | number; type?: string | number }[] } =
        {}
      Object.keys(groupedItems).forEach((groupKey) => {
        const filteredGroup = groupedItems[groupKey].filter((item) =>
          item.name?.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
        if (filteredGroup.length > 0) {
          filtered[groupKey] = filteredGroup
        }
      })
      return filtered
    }
  }, [items, searchQuery])

  const hasContent =
    (Array.isArray(filteredItems) && filteredItems.length > 0) ||
    (filteredItems && typeof filteredItems === 'object' && Object.keys(filteredItems).length > 0)

  const handleClear = () => {
    if (!searchable) {
      return
    }
    onClick?.('')
    setSearchQuery('')
    setTimeout(() => {
      if (id) {
        const input = document.getElementById(id)
        input?.focus()
      }
    }, 0)
  }

  const handleSelect = (value: string) => {
    if (!searchable) {
      return
    }
    onClick?.(value)
    setSearchQuery(value)
  }

  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    !disabled && setOpen(!isOpen)
  }

  // The chevron must not bubble to the container toggle: the two toggles would
  // cancel each other out and the menu could never be closed from the arrow.
  const handleChevronClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    handleToggle(e)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' && isOpen) {
      e.stopPropagation()
      setOpen(false)
      return
    }
    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      setOpen(!isOpen)
    }
  }

  const dropdownConfig = useMemo(() => {
    const clickHandler = searchable ? handleSelect : onClick

    if (searchable) {
      return {
        groupedItems: typeof filteredItems === 'object' && !Array.isArray(filteredItems) ? filteredItems : groupedItems,
        items: Array.isArray(filteredItems) ? filteredItems : items,
        clickHandler,
      }
    }

    return {
      groupedItems,
      items,
      clickHandler,
    }
  }, [filteredItems, groupedItems, items, onClick, handleSelect])

  const chevronIcon = (
    <IconButton
      id={chevronId}
      iconClass={'pe-none ds3-icon ' + (isOpen ? 'ds3-chevron-up' : 'ds3-chevron-down')}
      onClick={handleChevronClick}
    />
  )

  let dropdownButton: JSX.Element
  if (asDiv) {
    dropdownButton = (
      <div className="d-flex value-container" id={id}>
        <div className="ps-3 pe-2 value" id={divValueId}>
          {value}
        </div>
        {chevronIcon}
      </div>
    )
  } else if (searchable) {
    dropdownButton = (
      <TextInput
        id={id}
        type={type}
        value={searchQuery}
        placeholder={placeholder ?? _t('select-option')}
        postfix={chevronIcon}
        disabled={disabled ?? !!value}
        cursor="pointer"
        onChange={(e) => setSearchQuery(e.target.value)}
        clearable
        onClear={handleClear}
      />
    )
  } else {
    dropdownButton = (
      <TextInput
        id={id}
        type={type}
        value={value}
        placeholder={placeholder ?? _t('select-option')}
        postfix={chevronIcon}
        disabled={disabled}
        readOnly
        cursor="pointer"
      />
    )
  }

  return (
    <div
      className={'dropdown-container ' + (className ?? '')}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      ref={referenceElement}
      style={style}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-disabled={disabled}
    >
      {dropdownButton}
      {isOpen && (
        <Popper
          placement={'bottom'}
          referenceElement={referenceElement.current!}
          modifiers={[
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom-end'],
              },
            },
          ]}
        >
          {({ ref, style, placement }) => (
            <OutsideClickHandler
              onOutsideClick={(e) => {
                // Ignore clicks anywhere inside the dropdown (input, chevron,
                // value) — those have their own handlers. Matching by element
                // id is not reliable here: IconButton prefixes its DOM id, so
                // the chevron never matched and its mousedown closed the menu
                // only for the click to immediately reopen it.
                if (isOpen && !referenceElement.current?.contains(e.target as Node)) {
                  setOpen(false)
                }
              }}
            >
              <ul
                ref={ref}
                style={style}
                data-placement={placement}
                onClick={() => setOpen(false)}
                className={`dropdown-menu custom-dropdown-menu${isOpen ? ' show' : ''}`}
              >
                {searchable && !hasContent && <NoResult />}
                {groupedItems && (
                  <GroupedDropdownItems
                    groupedItems={dropdownConfig.groupedItems ?? {}}
                    onClick={dropdownConfig.clickHandler}
                  />
                )}
                {items && (
                  <DropdownItems
                    items={dropdownConfig.items ?? []}
                    onClick={dropdownConfig.clickHandler}
                    active={value}
                  />
                )}
                {!groupedItems && !items && children}
              </ul>
            </OutsideClickHandler>
          )}
        </Popper>
      )}
    </div>
  )
}

function GroupedDropdownItems({
  groupedItems,
  onClick,
}: {
  groupedItems: { [key: string]: { value: string | number; name?: string | number; type?: string | number }[] }
  onClick: undefined | ((value: string) => void)
}) {
  return Object.keys(groupedItems).map((group) => (
    <li key={group}>
      <ul style={{ padding: 0 }}>
        <Dropdown.ItemText as="li" className="dropdown-group-title text-m my-1">
          {group}
        </Dropdown.ItemText>
        {groupedItems[group].map((item) => (
          <Dropdown.Item
            key={item.value}
            as="li"
            value={item.value}
            onClick={() => onClick && onClick(item.value + '')}
          >
            {item.name}
          </Dropdown.Item>
        ))}
      </ul>
    </li>
  ))
}

function NoResult() {
  return (
    <div className="px-4">
      <Trans i18nKey={'similarities-not-found'}>
        Found no data matching current<span>filter</span>
      </Trans>
    </div>
  )
}

function DropdownItems({
  items,
  onClick,
  active,
}: {
  items: { value: string | number; name?: string | number }[]
  onClick: undefined | ((value: string) => void)
  active: string | undefined
}) {
  return items.map((item) => {
    const name = (item.name ?? item.value).toString()

    return (
      <Dropdown.Item
        key={item.value}
        as="li"
        value={item.value}
        onClick={() => onClick && onClick(item.value + '')}
        className={name === active ? 'active' : undefined}
      >
        {name}
      </Dropdown.Item>
    )
  })
}

export default DropdownSelect
